/**
 * A labelled number input for the operator console's two editors.
 *
 * Shared rather than copied because both of them owe the same debt to the shared zod schemas:
 * the value is held as typed text and the *schema* decides whether it is a number, so a
 * half-typed field is never silently coerced and the message an operator reads is the one the
 * server would have sent.
 */

import { Input } from '@/components/ui/input';

export interface NumberFieldProps {
  id: string;
  label: string;
  /** Shown when there is no error to show instead — e.g. the measured-safe range for this knob. */
  hint?: string;
  value: string;
  error: string | undefined;
  onChange: (value: string) => void;
}

export function NumberField({ id, label, hint, value, error, onChange }: NumberFieldProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <Input
        id={id}
        // `type="number"` and not the browser's own bounds: the schema owns every rule, and a
        // spinner that silently clamps would hide the message the schema wants to show.
        type="number"
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
